import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PmTemplateLibraryApiService, type ClientTypeLookupApiRow } from '../../../core/services/api/pm-template-library-api.service';
import { PmUserContextService } from '../../../core/services/pm-user-context.service';
import type { OnboardingKycClientType, RiskAssessmentClientType } from './pm-template-library.models';
import { TEMPLATE_CLIENT_TYPE_CODE } from './pm-template-library.models';

/**
 * Firm context shared by the Settings > Template Library tabs: who the firm is, which
 * CompanyMaster the template APIs target, and the client-type lookup.
 *
 * Provided on `PmWorkspaceComponent` (not root) so it lives and dies with the workspace,
 * exactly like the signals it replaced.
 */
@Injectable()
export class PmTemplateLibraryContextService {
  private readonly userContext = inject(PmUserContextService);
  private readonly pmTemplateLibraryApi = inject(PmTemplateLibraryApiService);

  /** Firm parent for directory / template APIs (`practice-context`). */
  readonly parentUserId = computed(() => this.userContext.firmParentUserIdForApi());
  /** Acting user for directory and practice APIs. */
  readonly actingUserId = computed(() => this.userContext.actingUserMasterIdForApi());

  /** Open Template Library tab; kept here so it survives leaving the section and coming back. */
  readonly activeTab = signal<string>('Email Templates');

  /** Client-type list returned by `GET /api/v1/lookups/client-types`. */
  readonly clientTypes = signal<readonly ClientTypeLookupApiRow[]>([]);
  /** Single-flight guard so we don't fire the lookup on every tab switch. */
  clientTypesLoaded = false;

  /** CompanyMaster.Id returned by the server after the first successful invoice template load. */
  readonly resolvedCompanyMasterId = signal(0);

  /** Same gate as directory/settings: allows empty `pmApiBaseUrl` (relative `/api/v1/...` + dev proxy). */
  apiBaseConfigured(): boolean {
    return this.actingUserId() > 0;
  }

  /** `CompanyMaster.Id` for template library APIs: resolved from server response first, then practice-context. */
  companyMasterId(): number {
    const fromResolved = this.resolvedCompanyMasterId();
    if (fromResolved > 0) return fromResolved;
    return this.userContext.practiceCompanyMasterId();
  }

  /** True if pm-api firm-level criteria endpoints have the parent they need. The CompanyMaster id is
   *  not required: those endpoints are scoped on parentUserId only, and practice-context no longer
   *  returns a CompanyMaster id, so requiring it blocked every load and save. */
  firmCriteriaConfigured(): boolean {
    return this.apiBaseConfigured() && this.parentUserId() > 0;
  }

  async loadClientTypes(force = false): Promise<void> {
    if (!force && this.clientTypes().length > 0) {
      this.clientTypesLoaded = true;
      return;
    }
    if (!this.apiBaseConfigured()) {
      return;
    }
    try {
      const rows = await firstValueFrom(this.pmTemplateLibraryApi.listClientTypes());
      this.clientTypes.set(rows ?? []);
      this.clientTypesLoaded = (rows ?? []).length > 0;
    } catch {
      // Lookup is best-effort; Services UI shows empty type lists until retry.
    }
  }

  /**
   * Resolves the human-readable label for a UI client-type key. Prefers the
   * label returned by the lookup API; falls back to the union value (e.g.
   * `'Soletrader'`) when the lookup hasn't loaded yet.
   */
  clientTypeLabelFor(t: OnboardingKycClientType | RiskAssessmentClientType): string {
    if (t === 'PracticeRiskAssessment') return 'Practice Risk Assessment';
    const code = TEMPLATE_CLIENT_TYPE_CODE[t as OnboardingKycClientType];
    const hit = this.clientTypes().find((r) => r.code === code);
    return hit?.label ?? (t as string);
  }
}
